import type { ReactNode } from 'react';
import { useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, ButtonBase, Menu, MenuItem, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { AM, AM_TEXT } from '../Dashboard/assignmentMonitoringTokens';
import {
  monthLabel,
  monthName,
  monthStateLabel,
  pct,
  plural,
  studentStatusLabel,
} from '../Dashboard/assignmentMonitoring';
import type { BenchmarkWindow, Group, MonthEntry, Requirement, StudentRow } from '../Dashboard/assignmentMonitoringApi';
import { Dot, MetBar, Pill, Skeleton } from '../Dashboard/components/AssignmentMonitoringUi';

export const COL = {
  name: { width: 200, flexShrink: 0, minWidth: 0 },
  grow: { flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' },
  num: { width: 112, flexShrink: 0, textAlign: 'right', ...AM_TEXT.body1 },
  chevron: { width: 16, flexShrink: 0 },
} as const;

export function RecordCard({
  title,
  sub,
  chip,
  right,
  testId,
  children,
}: {
  title: string;
  sub?: string;
  chip?: ReactNode;
  right?: ReactNode;
  testId: string;
  children: ReactNode;
}) {
  return (
    <Box
      component="section"
      data-testid={testId}
      sx={{ bgcolor: AM.surface, border: `1px solid ${AM.divider}`, borderRadius: '12px', p: '32px' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', pb: '16px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', ...AM_TEXT.h4, color: AM.ink }}>
          {title}
          {sub ? (
            <Typography component="span" sx={{ ...AM_TEXT.body1Semi, color: AM.muted }}>
              {sub}
            </Typography>
          ) : null}
          {chip}
        </Box>
        {right ? (
          <Box sx={{ ...AM_TEXT.body1, color: AM.muted, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '16px' }}>
            {right}
          </Box>
        ) : null}
      </Box>
      {children}
    </Box>
  );
}

export function TableHead({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        pb: '8px',
        borderBottom: `2px solid ${AM.ink}`,
        color: AM.muted,
        ...AM_TEXT.overline,
        '& .num': { width: 112, flexShrink: 0, textAlign: 'right', whiteSpace: 'nowrap' },
      }}
    >
      {children}
    </Box>
  );
}

const ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  py: '16px',
  borderBottom: `1px solid ${AM.divider}`,
  '&:last-of-type': { borderBottomColor: 'transparent' },
} as const;

export function LinkRow({ to, onClick, children }: { to: string; onClick?: () => void; children: ReactNode }) {
  return (
    <Box
      component={RouterLink}
      to={to}
      onClick={onClick}
      sx={{
        ...ROW_SX,
        cursor: 'pointer',
        mx: '-16px',
        px: '16px',
        color: AM.ink,
        textDecoration: 'none',
        '&:hover': { bgcolor: AM.actionHover },
      }}
    >
      {children}
      <ChevronRightIcon sx={{ ...COL.chevron, fontSize: 16, color: AM.muted }} />
    </Box>
  );
}

export function NameCell({ name, small, link }: { name: string; small: string; link?: string }) {
  return (
    <Box sx={COL.name}>
      <Typography sx={{ ...AM_TEXT.body1Bold, color: AM.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {name}
      </Typography>
      <Typography sx={{ ...AM_TEXT.body2Semi, color: AM.muted }}>{small}</Typography>
      {link ? <Typography sx={{ ...AM_TEXT.body2Semi, color: AM.primary }}>{link}</Typography> : null}
    </Box>
  );
}

export function Num({ value, zero, red, dash }: { value: ReactNode; zero?: boolean; red?: boolean; dash?: boolean }) {
  return (
    <Typography
      sx={{
        ...COL.num,
        color: red ? AM.red700 : zero || dash ? AM.disabled : AM.ink,
        fontWeight: red ? 700 : 400,
      }}
    >
      {value}
    </Typography>
  );
}

export function ShortfallCells({ group }: { group: Group }) {
  return (
    <>
      <Num value={group.notAssigned} zero={!group.notAssigned} />
      <Num value={group.notSubmitted} zero={!group.notSubmitted} />
      <Num value={group.notScored} zero={!group.notScored} />
    </>
  );
}

export function RequirementCard({ group, req }: { group: Group; req: Requirement }) {
  const row = (name: string, met: number, last?: boolean) => (
    <Box key={name} data-testid="requirement-row" sx={{ ...ROW_SX, ...(last ? { borderBottom: 0, pb: 0 } : {}) }}>
      <Typography sx={{ ...COL.name, ...AM_TEXT.body1Bold, color: AM.ink }}>{name}</Typography>
      <Box sx={COL.grow}>
        <Typography sx={{ ...AM_TEXT.body2Bold, color: AM.ink }}>{pct(met, group.students)}% met</Typography>
        <MetBar met={met} notMet={group.students - met} />
      </Box>
      <Box sx={{ width: 112, flexShrink: 0 }} />
      <Num value={met} />
      <Num value={group.students - met} />
      <Box sx={COL.chevron} />
    </Box>
  );
  const legend = (kind: 'met' | 'notmet', label: string) => (
    <Box className="num" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
      <Dot kind={kind} />
      {label}
    </Box>
  );
  return (
    <RecordCard title="Requirement" right={plural(group.students, 'student')} testId="requirement-card">
      <TableHead>
        <Box sx={COL.name}>Rule</Box>
        <Box sx={{ flex: '1 1 0' }}>Students</Box>
        <Box sx={{ width: 112, flexShrink: 0 }} />
        {legend('met', 'Met')}
        {legend('notmet', 'Not met')}
        <Box sx={COL.chevron} />
      </TableHead>
      {row(`Assign ${req.assign} per student`, group.assignedMet)}
      {row(`Score ${req.score} per student`, group.scoredMet, true)}
    </RecordCard>
  );
}

export function Collapsed({ label, open, onToggle, inset }: { label: string; open: boolean; onToggle: () => void; inset?: boolean }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: '16px',
        px: inset ? '16px' : 0,
        borderTop: inset ? 0 : `1px solid ${AM.divider}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', ...AM_TEXT.body1Bold, color: AM.ink }}>
        <Dot kind="met" />
        {label}
      </Box>
      <ButtonBase
        onClick={onToggle}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: AM.primary, fontFamily: AM.font, ...AM_TEXT.body1Bold, borderRadius: '4px', '&:hover': { textDecoration: 'underline' } }}
      >
        {open ? 'Hide' : 'Show'}
        <KeyboardArrowDownIcon sx={{ fontSize: 16, transform: open ? 'rotate(180deg)' : 'none' }} />
      </ButtonBase>
    </Box>
  );
}

export function EmptyRow({ children }: { children: ReactNode }) {
  return (
    <Typography sx={{ p: '40px 0 32px', textAlign: 'center', color: AM.muted, ...AM_TEXT.body1, fontWeight: 500 }}>
      {children}
    </Typography>
  );
}

const STATUS_DOT = { met: 'met', notAssigned: 'notstarted', notSubmitted: 'inprog', notScored: 'toscore' } as const;

export function StudentLine({ row, req, inset }: { row: StudentRow; req: Requirement; inset?: boolean }) {
  const blocker = row.status === 'met' ? null : row.status;
  return (
    <Box data-testid="student-row" sx={{ ...ROW_SX, py: '12px', px: inset ? '16px' : 0 }}>
      <NameCell name={`${row.firstName} ${row.lastName}`} small={`Grade ${row.grade}`} />
      <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px', ...AM_TEXT.body1, color: AM.ink }}>
        <Dot kind={STATUS_DOT[row.status]} />
        <span>{studentStatusLabel(row)}</span>
      </Box>
      <Num value={`${row.assigned} of ${req.assign}`} red={blocker === 'notAssigned'} />
      {row.assigned === 0 ? <Num value="—" dash /> : <Num value={`${row.submitted} of ${row.assigned}`} red={blocker === 'notSubmitted'} />}
      <Num value={`${row.scored} of ${req.score}`} red={blocker === 'notScored'} zero={Boolean(blocker) && blocker !== 'notScored'} />
      <Box sx={COL.chevron} />
    </Box>
  );
}

// Students who met are collapsed under "<n> students met · Show".
export function StudentSection({ rows, req, inset }: { rows: StudentRow[]; req: Requirement; inset?: boolean }) {
  const [open, setOpen] = useState(false);
  const notMet = rows.filter((r) => r.status !== 'met');
  const met = rows.filter((r) => r.status === 'met');
  return (
    <>
      {notMet.map((r) => (
        <StudentLine key={r.id} row={r} req={req} inset={inset} />
      ))}
      {met.length ? (
        <>
          <Collapsed label={plural(met.length, 'student') + ' met'} open={open} onToggle={() => setOpen((o) => !o)} inset={inset} />
          {open ? met.map((r) => <StudentLine key={r.id} row={r} req={req} inset={inset} />) : null}
        </>
      ) : null}
    </>
  );
}

export function MonthSelect({ current, months, hrefFor }: { current: string; months: MonthEntry[]; hrefFor: (month: string) => string }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="listbox"
        aria-expanded={open}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          borderRadius: '8px',
          border: `1px solid ${open ? AM.primary : AM.divider}`,
          bgcolor: AM.surface,
          color: AM.ink,
          fontFamily: AM.font,
          ...AM_TEXT.body1Semi,
          whiteSpace: 'nowrap',
          '&:hover': { bgcolor: AM.actionHover },
        }}
      >
        {monthLabel(current)}
        <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ list: { role: 'listbox' } }}
      >
        {[...months].reverse().map((m) => (
          <MenuItem
            key={m.month}
            component={RouterLink}
            to={hrefFor(m.month)}
            role="option"
            aria-selected={m.month === current}
            selected={m.month === current}
            onClick={() => setAnchor(null)}
            sx={{ display: 'flex', alignItems: 'center', gap: '12px', ...AM_TEXT.body1, color: AM.ink, minWidth: 260 }}
          >
            <CheckIcon sx={{ fontSize: 16, visibility: m.month === current ? 'visible' : 'hidden' }} />
            <Box component="span" sx={{ flex: 1 }}>
              {monthLabel(m.month)}
            </Box>
            <Typography component="small" sx={{ ...AM_TEXT.body2, color: AM.muted }}>
              {monthStateLabel(m)}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export function SkippedCard({ month, window }: { month: string; window: BenchmarkWindow }) {
  const name = monthName(month);
  return (
    <Box component="section" sx={{ bgcolor: AM.surface, border: `1px solid ${AM.divider}`, borderRadius: '12px', p: '32px' }}>
      <Typography sx={{ ...AM_TEXT.h4, color: AM.ink }}>No requirement in {name}</Typography>
      <Typography sx={{ ...AM_TEXT.body1, color: AM.muted, mt: '8px' }}>
        The {window.kind} window covers every day of the month, so {name} doesn’t count toward the requirement.
      </Typography>
    </Box>
  );
}

export function LoadingCard({ month }: { month: string }) {
  return (
    <Box component="section" sx={{ bgcolor: AM.surface, border: `1px solid ${AM.divider}`, borderRadius: '12px', p: '32px', display: 'flex', flexDirection: 'column' }}>
      <Typography role="status" sx={{ ...AM_TEXT.body2, color: AM.muted, pb: '20px' }}>
        Loading the {monthName(month)} record…
      </Typography>
      {[62, 78, 54, 70].map((w) => (
        <Box key={w} sx={{ display: 'flex', alignItems: 'center', gap: '24px', py: '20px', borderBottom: `1px solid ${AM.divider}`, '&:last-of-type': { borderBottom: 0 } }}>
          <Box sx={{ width: `${w}%`, mr: 'auto' }}>
            <Skeleton />
          </Box>
          <Skeleton width={160} />
          <Skeleton width={64} />
        </Box>
      ))}
    </Box>
  );
}

export function ErrorCard({ month }: { month: string }) {
  return (
    <Box component="section" sx={{ bgcolor: AM.surface, border: `1px solid ${AM.divider}`, borderRadius: '12px', p: '32px' }}>
      <Typography sx={{ ...AM_TEXT.body1, color: AM.muted }}>
        The {monthName(month)} record couldn’t load right now. Try again in a moment.
      </Typography>
    </Box>
  );
}

export function OutlineButton({ label, icon, onClick }: { label: string; icon?: ReactNode; onClick: () => void }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        boxSizing: 'border-box',
        height: '40px',
        padding: '0 16px',
        borderRadius: '8px',
        border: `1px solid ${AM.primary}`,
        bgcolor: AM.surface,
        color: AM.primary,
        fontFamily: AM.font,
        ...AM_TEXT.body1Bold,
        whiteSpace: 'nowrap',
        '&:hover': { bgcolor: AM.indigo50 },
      }}
    >
      {icon}
      {label}
    </ButtonBase>
  );
}

export function MetPill({ met }: { met: boolean }) {
  return met ? <Pill label="Met" tone="indigo" /> : <Pill label="Not met" tone="red" />;
}
